// Wealth (BWM) tab. Lists this household's BWM engagements with each
// person's wealth-specific revenue breakdown (monthly recurring,
// investment fee, AUM, optional tax plan fee). Includes a placeholder
// for the future SEI custodian feed integration.
//
// Phase C port of the legacy modal's BWMTab. Fields renamed to
// snake_case to match the API.

import { EngagementsList, PlaceholderCard, EmptyState, AddServiceCTA } from './_engagementsList';
import { formatCurrency } from '../../../utils/formatting';

const TIER_LABELS = {
  'guided-wealth': 'Guided Wealth',
  'total-wealth':  'Total Wealth',
  'investment-only': 'Investment Only',
  'tax-plan':      'Tax Plan',
};

export default function WealthTab({ members, people }) {
  const bwm = members.filter(m => m.division === 'bwm');
  const peopleById = Object.fromEntries(people.map(p => [p.id, p]));

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <h2 className="text-[15px] font-bold text-navy">Wealth (BWM) Engagements</h2>
            <p className="text-[11px] text-[#888] mt-0.5">
              {bwm.length} {bwm.length === 1 ? 'engagement' : 'engagements'}
            </p>
          </div>
          <AddServiceCTA division="bwm" label="+ Add Wealth Service" />
        </div>

        {bwm.length === 0 ? (
          <EmptyState label="No BWM service for this household." />
        ) : (
          <EngagementsList
            engagements={bwm}
            peopleById={peopleById}
            renderDetail={(e) => (
              <>
                <DetailChip label="Tier" value={TIER_LABELS[e.service_tier] || e.service_tier || '—'} />
                {Number(e.monthly_revenue) > 0 && (
                  <DetailChip label="Monthly" value={`${formatCurrency(e.monthly_revenue)}/mo`} />
                )}
                {Number(e.investment_fee_annual) > 0 && (
                  <DetailChip label="Investment fee" value={`${formatCurrency(e.investment_fee_annual)}/yr`} />
                )}
                {Number(e.tax_plan_fee) > 0 && (
                  <DetailChip label="Tax plan" value={formatCurrency(e.tax_plan_fee)} />
                )}
                {Number(e.aum) > 0 && (
                  <DetailChip label="AUM" value={formatCurrency(e.aum)} accent />
                )}
              </>
            )}
          />
        )}
      </div>

      <PlaceholderCard
        title="Investment Accounts (SEI)"
        body="Account-level holdings, performance, and AUM history will populate here once the SEI custodian feed is connected."
      />
    </div>
  );
}

function DetailChip({ label, value, accent }) {
  return (
    <span className="inline-flex items-baseline gap-1.5 text-[11px]">
      <span className="text-[#888] uppercase tracking-wider text-[9px]">{label}</span>
      <span className={accent ? 'text-deep-teal font-bold' : 'text-navy font-bold'}>{value}</span>
    </span>
  );
}
